import * as React from 'react'
import {
  Show,
  TabbedShowLayout,
  Tab,
  TextField,
  BooleanField,
  DateField,
  FunctionField,
  TopToolbar,
  ListButton,
  EditButton,
  useGetIdentity,
} from 'react-admin'
import { useTranslate } from 'ra-core'
import { Box, ButtonGroup, Button, Typography, Chip } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import ViewListIcon from '@material-ui/icons/ViewList'
import ViewCarouselIcon from '@material-ui/icons/ViewCarousel'
import PropTypes from 'prop-types'
import StatusChip from '../../components/StatusChip'
import CampaignQuestionFlow from '../../components/CampaignQuestionFlow'
import CampaignQuestionTable from '../../components/CampaignQuestionTable'

const useStyles = makeStyles(theme => ({
  summaryCard: {
    padding: theme.spacing(3),
    borderRadius: 16,
    border: '1px solid #e2e8f0',
    backgroundColor: '#fff',
    marginBottom: theme.spacing(3),
  },
  metaRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  viewToggle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
}))

const CampaignFlowPanel = ({ record, locale }) => {
  const classes = useStyles()
  const translate = useTranslate()
  const [viewMode, setViewMode] = React.useState('cards')

  if (!record) return null

  return (
    <Box>
      <Box className={classes.viewToggle}>
        <Typography variant="h6">
          {translate('surveys.campaigns.flow_title')}
        </Typography>
        <ButtonGroup variant="outlined" color="primary" size="small">
          <Button
            startIcon={<ViewCarouselIcon />}
            variant={viewMode === 'cards' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('cards')}
          >
            {translate('surveys.campaigns.view_cards')}
          </Button>
          <Button
            startIcon={<ViewListIcon />}
            variant={viewMode === 'table' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('table')}
          >
            {translate('surveys.campaigns.view_table')}
          </Button>
        </ButtonGroup>
      </Box>
      {viewMode === 'cards' ? (
        <CampaignQuestionFlow
          questionIds={record.questionIds}
          questionList={record.questionList}
          locale={locale}
        />
      ) : (
        <CampaignQuestionTable
          questionIds={record.questionIds}
          questionList={record.questionList}
          locale={locale}
        />
      )}
    </Box>
  )
}

CampaignFlowPanel.propTypes = {
  record: PropTypes.object,
  locale: PropTypes.string.isRequired,
}

const CampaignSummary = ({ record, translate }) => {
  const classes = useStyles()
  if (!record) return null

  const questionCount = (record.questionIds || []).length

  return (
    <Box className={classes.summaryCard}>
      <Typography variant="h5" gutterBottom>
        {record.name}
      </Typography>
      <Box className={classes.metaRow}>
        <Chip label={`ID ${record.id}`} size="small" />
        <Chip label={record.country} size="small" variant="outlined" />
        <StatusChip
          value={record.status}
          activeLabel={translate('surveys.common.active')}
          inactiveLabel={translate('surveys.common.desactive')}
        />
        <Chip
          label={translate('surveys.campaigns.question_count', {
            count: questionCount,
          })}
          size="small"
          style={{ backgroundColor: '#e0f2fe', color: '#0369a1' }}
        />
      </Box>
    </Box>
  )
}

CampaignSummary.propTypes = {
  record: PropTypes.object,
  translate: PropTypes.func.isRequired,
}

const CampaignsShowActions = ({ basePath, data }) => {
  const translate = useTranslate()
  return (
    <TopToolbar>
      <ListButton basePath={basePath} />
      <EditButton
        basePath={basePath}
        label={translate('ra.action.edit')}
        record={data}
      />
    </TopToolbar>
  )
}

CampaignsShowActions.propTypes = {
  basePath: PropTypes.any,
  data: PropTypes.any,
}

const CampaignsShow = props => {
  const translate = useTranslate()
  const { identity, loading: identityLoading } = useGetIdentity()
  if (identityLoading) return <>Loading...</>
  const locale = identity.country.toLowerCase()

  return (
    <Show actions={<CampaignsShowActions />} {...props}>
      <TabbedShowLayout>
        <Tab label={translate('surveys.campaigns.tab_summary')}>
          <FunctionField
            render={record => (
              <CampaignSummary record={record} translate={translate} />
            )}
          />
          <TextField source="id" />
          <TextField source="name" />
          <TextField source="country" />
          <BooleanField source="status" />
          <DateField source="createdAt" showTime />
        </Tab>
        <Tab label={translate('surveys.campaigns.tab_flow')} path="flow">
          <FunctionField
            render={record => (
              <CampaignFlowPanel record={record} locale={locale} />
            )}
          />
        </Tab>
      </TabbedShowLayout>
    </Show>
  )
}

export default CampaignsShow
